/**
 * Blox-Design plan page typography (Figma node 75644:14192, 75644:12513).
 *
 * Families map to Chakra theme font tokens in `styles/theme/fonts.ts`.
 * Medium / semibold use Roboto Regular with weight — only 400/700 files are loaded.
 */
export const PLAN_FONT = {
  comfortaaBold: 'bold',
  roboto: 'regular_roboto',
  robotoBold: 'bold_roboto',
} as const;

export const PLAN_TEXT = {
  /**
   * 20px/600, matching DimensionsRevamp's header rather than the 24px/700 on
   * the Figma artboard. That module stepped its own title down for a reason
   * that applies here identically: a page title in a data tool is a wayfinding
   * label, not a headline, and at 24px/bold it competed with the KPI row
   * directly beneath it — the numbers are what the user came to read.
   */
  pageTitle: {
    fontFamily: PLAN_FONT.comfortaaBold,
    fontSize: '20px',
    fontWeight: 600,
    lineHeight: 'normal',
  },
  metaAccent: {
    fontFamily: PLAN_FONT.roboto,
    fontSize: '12px',
    fontWeight: 500,
    lineHeight: 'normal',
  },
  body: {
    fontFamily: PLAN_FONT.roboto,
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: 'normal',
  },
  bodyMedium: {
    fontFamily: PLAN_FONT.roboto,
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: 'normal',
  },
  bodySemibold: {
    fontFamily: PLAN_FONT.roboto,
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: 'normal',
  },
  toolbarTab: {
    fontFamily: PLAN_FONT.roboto,
    fontSize: '13px',
    fontWeight: 500,
    lineHeight: 'normal',
  },
  badge: {
    fontFamily: PLAN_FONT.roboto,
    fontSize: '10px',
    fontWeight: 500,
    lineHeight: 'normal',
  },
  labelSm: {
    fontFamily: PLAN_FONT.roboto,
    fontSize: '12px',
    fontWeight: 400,
    lineHeight: 'normal',
  },
  labelSmMedium: {
    fontFamily: PLAN_FONT.roboto,
    fontSize: '12px',
    fontWeight: 500,
    lineHeight: 'normal',
  },
  kpiFeaturedLabel: {
    fontFamily: PLAN_FONT.roboto,
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: 'normal',
  },
  kpiFeaturedValue: {
    fontFamily: PLAN_FONT.roboto,
    fontSize: '24px',
    fontWeight: 600,
    lineHeight: 'normal',
  },
  kpiFeaturedSubtitle: {
    fontFamily: PLAN_FONT.roboto,
    fontSize: '12px',
    fontWeight: 500,
    lineHeight: 'normal',
  },
  kpiLabel: {
    fontFamily: PLAN_FONT.roboto,
    fontSize: '12px',
    fontWeight: 400,
    lineHeight: 'normal',
  },
  /**
   * The KPI card's headline number.
   *
   * Comfortaa rather than Roboto: this is the one figure the row exists to
   * show, and setting it in the display face — the same one the page title
   * uses — separates it from the Roboto labels and captions around it instead
   * of relying on size alone to carry the hierarchy.
   */
  kpiValue: {
    fontFamily: PLAN_FONT.comfortaaBold,
    fontSize: '20px',
    fontWeight: 600,
    lineHeight: '1.15',
  },
  panelTitle: {
    fontFamily: PLAN_FONT.roboto,
    fontSize: '20px',
    fontWeight: 600,
    lineHeight: '1.2',
  },
  /**
   * Inputs/What-Ifs sidebar title. Was 20px, the same size as the page
   * title — too heavy for a side-panel label sitting this close to its own
   * subtitle and cards. 16px keeps the Comfortaa display face without
   * competing with the actual page title.
   */
  inputsPanelTitle: {
    fontFamily: PLAN_FONT.comfortaaBold,
    fontSize: '16px',
    fontWeight: 600,
    lineHeight: 'normal',
  },
  modalTitle: {
    fontFamily: PLAN_FONT.roboto,
    fontSize: '16px',
    fontWeight: 700,
    lineHeight: '1.2',
  },
  modalSubtitle: {
    fontFamily: PLAN_FONT.roboto,
    fontSize: '12px',
    fontWeight: 400,
    lineHeight: '1.5',
  },
  fieldLabel: {
    fontFamily: PLAN_FONT.roboto,
    fontSize: '12px',
    fontWeight: 600,
    lineHeight: 'normal',
  },
  tableHeader: {
    fontFamily: PLAN_FONT.roboto,
    fontSize: '12px',
    fontWeight: 600,
    lineHeight: 'normal',
  },
  tableCell: {
    fontFamily: PLAN_FONT.roboto,
    fontSize: '12px',
    fontWeight: 400,
    lineHeight: 'normal',
  },
  tableCellMedium: {
    fontFamily: PLAN_FONT.roboto,
    fontSize: '12px',
    fontWeight: 500,
    lineHeight: 'normal',
  },
  tableCellBold: {
    fontFamily: PLAN_FONT.robotoBold,
    fontSize: '12px',
    fontWeight: 700,
    lineHeight: 'normal',
  },
  /** Figma 75644:8226 — metric column + period headers */
  tableComfortaa: {
    fontFamily: PLAN_FONT.comfortaaBold,
    fontSize: '12px',
    fontWeight: 700,
    lineHeight: 'normal',
  },
  /** Figma 75644:8226 — table cell values */
  tableComfortaaCell: {
    fontFamily: PLAN_FONT.roboto,
    fontSize: '12px',
    fontWeight: 400,
    lineHeight: 'normal',
  },
  button: {
    fontFamily: PLAN_FONT.roboto,
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: 'normal',
  },
  caption: {
    fontFamily: PLAN_FONT.roboto,
    fontSize: '12px',
    fontWeight: 400,
    lineHeight: '1.4',
  },
} as const;

export type PlanTextVariant = keyof typeof PLAN_TEXT;
